import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, addDoc, Timestamp, deleteDoc, query, where, getDocs, deleteField, arrayUnion } from 'firebase/firestore';
import { db, createDirectReturnFromFoundItem } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from './ConfirmationDialog';
import PotentialMatches from './PotentialMatches';
import PackageSizeSelector from './PackageSizeSelector';
import { getCountryFlag, getCountryNameFromCode, countries as countryList } from '../utils/countryUtils';
import { DEFAULT_QUIKBAK_FEE } from '../utils/shippingUtils';
import { fetchShippingEstimate } from '../utils/shippingApi';

export default function ItemDetails() {
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [matchedLostItem, setMatchedLostItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isStaffOrAdmin, setIsStaffOrAdmin] = useState(false);
  const [showUnmatchDialog, setShowUnmatchDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showUndoDiscardDialog, setShowUndoDiscardDialog] = useState(false);
  const [showPackageSizeSelector, setShowPackageSizeSelector] = useState(false);
  const [selectedPackageSize, setSelectedPackageSize] = useState(null);
  const { id, hotelId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userClaims, setUserClaims] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [shippingQuote, setShippingQuote] = useState(null);
  const [shippingQuoteLoading, setShippingQuoteLoading] = useState(false);
  const [shippingQuoteError, setShippingQuoteError] = useState('');
  const [showMatchedDetails, setShowMatchedDetails] = useState(false);
  const showPricing = !isStaffOrAdmin;
  const [noteText, setNoteText] = useState('');
  const [ownerEditMode, setOwnerEditMode] = useState(false);
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [ownerDraft, setOwnerDraft] = useState({
    firstName: '',
    surname: '',
    dateLost: '',
    phone: '',
    email: '',
    address: '',
    country: '',
    organisation: '',
  });
  const [showDirectReturnDialog, setShowDirectReturnDialog] = useState(false);
  const [directReturnSubmitting, setDirectReturnSubmitting] = useState(false);

  const normalizeOwnerDate = useCallback((value) => {
    if (!value) {
      return '';
    }
    if (value instanceof Timestamp) {
      return value.toDate().toISOString().slice(0, 10);
    }
    if (value instanceof Date) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return '';
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
      }
    }
    return '';
  }, []);

  const toOwnerDraft = useCallback((sourceItem) => ({
    firstName: sourceItem?.firstName || '',
    surname: sourceItem?.surname || '',
    dateLost: normalizeOwnerDate(sourceItem?.dateLost),
    phone: sourceItem?.phone || '',
    email: sourceItem?.email || '',
    address: sourceItem?.address || '',
    country: sourceItem?.country || '',
    organisation: sourceItem?.organisation || '',
  }), [normalizeOwnerDate]);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

  useEffect(() => {
    async function checkUserRole() {
      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult();
        const isAdmin = idTokenResult.claims.admin === true;
        const isStaff = idTokenResult.claims.staff === true;
        setIsStaffOrAdmin(isAdmin || isStaff);
        setUserClaims(idTokenResult.claims);
      }
    }
    checkUserRole();
  }, [currentUser]);

  useEffect(() => {
    async function fetchItem() {
      try {
        const docRef = doc(db, "found-items", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const itemData = { id: docSnap.id, ...docSnap.data() };
          setItem(itemData);
          
          // If there's a match ID, fetch match data and related lost item
          if (itemData.matchId) {
            try {
              // Fetch match data
              const matchDocRef = doc(db, "matches", itemData.matchId);
              const matchDocSnap = await getDoc(matchDocRef);
                if (matchDocSnap.exists()) {
                  const matchData = { id: matchDocRef.id, ...matchDocSnap.data() };
                  setMatchData(matchData);
                  setSelectedPackageSize(matchData.packageSize || null);
                  const paymentComplete = matchData.paymentStatus === 'succeeded' || matchData.status === 'ready-to-ship' || matchData.status === 'shipped';
                  setShowPackageSizeSelector(!paymentComplete && matchData.status === 'matched' && !matchData.packageSize && userClaims?.admin === true);

                  // Fetch lost item using ID from match data
                  const lostItemRef = doc(db, "lost-items", matchData.lostItemId);
                  const lostItemSnap = await getDoc(lostItemRef);
                  if (lostItemSnap.exists()) {
                    setMatchedLostItem({ id: lostItemSnap.id, ...lostItemSnap.data() });
                  }
                }
            } catch (matchError) {
              console.error('Error fetching match data:', matchError);
            }
          }

          // Mark as viewed if user is staff/admin and item is unviewed
          if (isStaffOrAdmin && !itemData.viewed) {
            await updateDoc(docRef, { viewed: true });
            console.log('Marked found item as viewed:', id);
          }

          // For finder user information, we use the finderName field which already contains
          // the user's display name or email when the item was registered
          // No need to fetch from a separate users collection
        } else {
          setError(t('foundItemDetails.messages.itemNotFound'));
        }
      } catch (err) {
        setError(t('foundItemDetails.messages.errorFetching') + err.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (id && isStaffOrAdmin !== null) {
      fetchItem();
    }
  }, [id, isStaffOrAdmin, t, userClaims?.admin]);

  useEffect(() => {
    if (item && !ownerEditMode) {
      setOwnerDraft(toOwnerDraft(item));
    }
  }, [item, ownerEditMode, toOwnerDraft]);

  const fallbackCountryName = matchedLostItem?.country || null;
  const fallbackCountryCode = fallbackCountryName
    ? (countryList.find((c) => c.name === fallbackCountryName)?.isoCode || '').toUpperCase()
    : '';

  const isPaymentFinalised = matchData?.paymentStatus === 'succeeded' || matchData?.status === 'ready-to-ship' || matchData?.status === 'shipped';
  const canUnmatch = Boolean(isStaffOrAdmin && matchData && !isPaymentFinalised);
  const canEditPackage = Boolean(userClaims?.admin === true && matchData && !isPaymentFinalised);
  const canEditOwner = Boolean(userClaims?.admin === true && item && item.status !== 'disposed' && !item.matchId);
  const canCreateDirectReturn = Boolean(userClaims?.admin === true && item && item.status !== 'disposed' && !item.matchId && !ownerEditMode);

  const ownerFirstName = (ownerEditMode ? ownerDraft.firstName : item?.firstName || '').trim();
  const ownerSurname = (ownerEditMode ? ownerDraft.surname : item?.surname || '').trim();
  const ownerEmail = (ownerEditMode ? ownerDraft.email : item?.email || '').trim();
  const ownerInfoReadyForDirectReturn = ownerFirstName !== '' && ownerSurname !== '' && isValidEmail(ownerEmail);

  useEffect(() => {
    if (!canUnmatch && showUnmatchDialog) {
      setShowUnmatchDialog(false);
    }
  }, [canUnmatch, showUnmatchDialog]);

  useEffect(() => {
    if (userClaims?.admin === true && matchData && !matchData.packageSize && !isPaymentFinalised) {
      setShowPackageSizeSelector(true);
    }
  }, [userClaims?.admin, matchData, matchData?.packageSize, isPaymentFinalised]);

  useEffect(() => {
    if (!canEditPackage && showPackageSizeSelector) {
      setShowPackageSizeSelector(false);
    }
  }, [canEditPackage, showPackageSizeSelector]);

  useEffect(() => {
    setShowMatchedDetails(false);
  }, [matchData?.id]);

  useEffect(() => {
    if (!isStaffOrAdmin) {
      setShippingQuote(null);
      setShippingQuoteError('');
      setShippingQuoteLoading(false);
      return;
    }

    if (!matchData?.id || !matchData?.packageSize) {
      setShippingQuote(null);
      return;
    }

    const preferredCountryName =
      (matchData?.shippingAddress?.country && matchData.shippingAddress.country.trim() !== '') ? matchData.shippingAddress.country :
      (matchData?.selectedShippingCountryName && matchData.selectedShippingCountryName.trim() !== '') ? matchData.selectedShippingCountryName :
      (matchData?.destinationCountry && matchData.destinationCountry.trim() !== '') ? matchData.destinationCountry :
      fallbackCountryName ||
      null;

    if (!preferredCountryName) {
      setShippingQuote(null);
      return;
    }

    const preferredCountryCode =
      (matchData?.shippingAddress?.countryCode && matchData.shippingAddress.countryCode.trim() !== '') ? matchData.shippingAddress.countryCode :
      (matchData?.selectedShippingCountryCode && matchData.selectedShippingCountryCode.trim() !== '') ? matchData.selectedShippingCountryCode :
      (matchData?.destinationCountryCode && matchData.destinationCountryCode.trim() !== '') ? matchData.destinationCountryCode :
      fallbackCountryCode ||
      '';

    let ignore = false;
    setShippingQuoteLoading(true);
    setShippingQuoteError('');

    fetchShippingEstimate({
      matchId: matchData.id,
      countryName: preferredCountryName,
      countryCode: preferredCountryCode,
      currency: matchData?.paymentCurrency || 'jpy',
    })
      .then((quote) => {
        if (!ignore) {
          setShippingQuote(quote);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error('Failed to fetch shipping estimate:', error);
          setShippingQuote(null);
          setShippingQuoteError(error?.message || t('payment.messages.error'));
        }
      })
      .finally(() => {
        if (!ignore) {
          setShippingQuoteLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [fallbackCountryCode, fallbackCountryName, isStaffOrAdmin, matchData?.destinationCountry, matchData?.destinationCountryCode, matchData?.id, matchData?.packageSize, matchData?.paymentCurrency, matchData?.selectedShippingCountryCode, matchData?.selectedShippingCountryName, matchData?.shippingAddress?.country, matchData?.shippingAddress?.countryCode, t]);


  const handleMatch = async (lostItem) => {
    try {
      // Create match document first
      const matchesRef = collection(db, "matches");
      const now = Timestamp.now();
      const matchDoc = await addDoc(matchesRef, {
        lostItemId: lostItem.id,
        foundItemId: id,
        hotelId: hotelId,
        status: 'matched',
        matchedAt: now,
        createdAt: now,
        updatedAt: now
      });

      // Update the found item with match ID and status
      const foundItemRef = doc(db, "found-items", id);
      await updateDoc(foundItemRef, {
        matchId: matchDoc.id,
        status: 'matched'
      });

      // Update the lost item with match ID and status
      const lostItemRef = doc(db, "lost-items", lostItem.id);
      await updateDoc(lostItemRef, {
        matchId: matchDoc.id,
        status: 'matched'
      });

      // Update local state
      setItem(prev => ({ ...prev, matchId: matchDoc.id, status: 'matched' }));
      setMatchedLostItem(lostItem);
      setMatchData({
        id: matchDoc.id,
        lostItemId: lostItem.id,
        foundItemId: id,
        status: 'matched',
        matchedAt: now,
        createdAt: now,
        updatedAt: now
      });
      setShowPackageSizeSelector(true);

      // Redirect to the newly created match page
      navigate(`/${hotelId}/matches/${matchDoc.id}`);
    } catch (err) {
      setError(t('foundItemDetails.messages.errorMatching') + err.message);
    }
  };

  const handleUnmatch = async () => {
    if (!isStaffOrAdmin || !canUnmatch) {
      setShowUnmatchDialog(false);
      return;
    }

    try {
      // Delete the match document
      if (item.matchId) {
        const matchRef = doc(db, "matches", item.matchId);
        await deleteDoc(matchRef);
      }

      // Update the found item - remove match reference and set status to pending
      const foundItemRef = doc(db, "found-items", id);
      await updateDoc(foundItemRef, {
        matchId: null,
        status: 'pending'
      });

      // Update the lost item - remove match reference and set status to pending
      if (matchData?.lostItemId) {
        const lostItemRef = doc(db, "lost-items", matchData.lostItemId);
        await updateDoc(lostItemRef, {
          matchId: null,
          status: 'pending'
        });
      }

      // Update local state
      setItem(prev => ({
        ...prev,
        matchId: null,
        status: 'pending'
      }));
      setMatchedLostItem(null);
      setMatchData(null);
      setShowPackageSizeSelector(false);
      setSelectedPackageSize(null);
      setShowUnmatchDialog(false);
    } catch (err) {
      setError(t('foundItemDetails.messages.errorUnmatching') + err.message);
    }
  };

  const handlePackageSizeSelect = async (size, weight) => {
    if (!canEditPackage) {
      return;
    }
    try {
      // Get the country from the lost item
      const country = matchedLostItem.country;
      if (!country) {
        console.error("No country specified in lost item");
        return;
      }

      const now = Timestamp.now();
      const isFinalised = matchData?.status === 'ready-to-ship' || matchData?.status === 'shipped';

      const matchRef = doc(db, "matches", item.matchId);
      const baseUpdate = {
        packageSize: size,
        updatedAt: now,
      };

      const firestoreUpdate = { ...baseUpdate };
      const localUpdate = { ...baseUpdate };

      if (weight && weight.trim() !== '') {
        const parsedWeight = parseFloat(weight);
        if (!Number.isNaN(parsedWeight)) {
          firestoreUpdate.packageWeight = parsedWeight;
          localUpdate.packageWeight = parsedWeight;
        }
      }

      if (!isFinalised) {
        firestoreUpdate.status = 'waiting-for-payment';
        firestoreUpdate.paymentTotal = deleteField();
        firestoreUpdate.destinationCountry = deleteField();
        firestoreUpdate.destinationCountryCode = deleteField();
        firestoreUpdate.selectedShippingCountryName = deleteField();
        firestoreUpdate.selectedShippingCountryCode = deleteField();
        firestoreUpdate.shippingAddress = deleteField();
        firestoreUpdate.readyToShipCode = deleteField();
      }

      await updateDoc(matchRef, firestoreUpdate);

      if (!isFinalised) {
        const foundUpdates = { status: 'waiting-for-payment', updatedAt: now };
        await updateDoc(doc(db, "found-items", item.id), foundUpdates);

        if (matchData?.lostItemId) {
          updateDoc(doc(db, "lost-items", matchData.lostItemId), {
            status: 'waiting-for-payment',
            updatedAt: now
          }).catch(error => console.warn('Failed to update lost item status to waiting-for-payment:', error));
        }
      }

      setMatchData((prev) => {
        if (!prev) {
          return prev;
        }
        const next = { ...prev, ...localUpdate };
        if (!isFinalised) {
          next.status = 'waiting-for-payment';
          next.paymentStatus = 'pending';
          delete next.paymentTotal;
          delete next.destinationCountry;
          delete next.destinationCountryCode;
          delete next.selectedShippingCountryName;
          delete next.selectedShippingCountryCode;
          delete next.shippingAddress;
          delete next.readyToShipCode;
        }
        return next;
      });

      setSelectedPackageSize(size);
      setShippingQuote(null);
      setShippingQuoteError('');
      setShowPackageSizeSelector(false);
    } catch (error) {
      console.error("Error updating package size:", error);
    }
  };

  const shippingCostValue = shippingQuote?.shippingCost ?? null;

  const quikbakFeeValue = shippingQuote?.quikbakFee ?? DEFAULT_QUIKBAK_FEE;

  const totalChargeValue = typeof matchData?.paymentTotal === 'number'
    ? matchData.paymentTotal
    : (shippingQuote?.totalAmount ?? null);

  const destinationCountryCode = shippingQuote?.countryCode
    || matchData?.shippingAddress?.countryCode
    || matchData?.destinationCountryCode
    || matchData?.selectedShippingCountryCode
    || fallbackCountryCode;

  const destinationCountryName = shippingQuote?.countryName
    || getCountryNameFromCode(destinationCountryCode)
    || matchData?.shippingAddress?.country
    || matchData?.destinationCountry
    || matchData?.selectedShippingCountryName
    || fallbackCountryName
    || t('foundItemDetails.values.noData');

  const handleDelete = async () => {
    setShowDeleteDialog(false);
    setLoading(true);
    try {
      // Delete associated match if exists
      if (item.matchId) {
         const matchRef = doc(db, 'matches', item.matchId);
         await deleteDoc(matchRef);
         console.log('Associated match deleted:', item.matchId);

         // Also update the corresponding lost item's status
         const lostItemQuery = query(collection(db, 'lost-items'), where('matchId', '==', item.matchId));
         const lostItemSnapshot = await getDocs(lostItemQuery);
         lostItemSnapshot.forEach(async (lostDoc) => {
            await updateDoc(lostDoc.ref, { matchId: null, status: 'pending' });
         });
      }

      await deleteDoc(doc(db, 'found-items', id));
      navigate(`/${hotelId}/admin`); // Navigate back to admin using hotelId
    } catch (err) {
      setError(t('foundItemDetails.messages.errorDeleting'));
      console.error(err);
      setLoading(false);
    }
  };

  const handleDiscard = async () => {
    setShowDiscardDialog(false);
    setLoading(true);
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const itemRef = doc(db, 'found-items', id);

      // Get comprehensive discarder info from Firestore user document
      let disposerName = '';
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          disposerName = userData.displayName || userData.name || '';
        }
      } catch (userFetchError) {
        console.warn('Could not fetch user display name from Firestore:', userFetchError);
      }

      // Fallback to Auth display name or email if Firestore fetch fails
      if (!disposerName) {
        disposerName = currentUser.displayName || currentUser.email || 'Unknown User';
      }

      const disposerEmail = currentUser.email || '';
      const disposerId = currentUser.uid || '';

      console.log('Setting disposer info:', { disposerName, disposerEmail, disposerId });

      await updateDoc(itemRef, {
        status: 'disposed',
        discardedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        disposer: disposerName,
        disposerEmail: disposerEmail,
        disposerId: disposerId
      });

      // Update local state
      setItem(prev => ({
        ...prev,
        status: 'disposed',
        discardedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        disposer: disposerName,
        disposerEmail: disposerEmail,
        disposerId: disposerId
      }));

      setLoading(false);
    } catch (err) {
      setError(t('foundItemDetails.messages.errorDiscarding'));
      console.error('Error discarding item:', err);
      setLoading(false);
    }
  };

  const handleUndoDiscard = async () => {
    setShowUndoDiscardDialog(false);
    setLoading(true);
    try {
      const itemRef = doc(db, 'found-items', id);
      await updateDoc(itemRef, {
        status: 'pending',
        discardedAt: null,
        updatedAt: Timestamp.now()
      });

      // Update local state
      setItem(prev => ({
        ...prev,
        status: 'pending',
        discardedAt: null,
        updatedAt: Timestamp.now()
      }));

      setLoading(false);
    } catch (err) {
      setError(t('foundItemDetails.messages.errorDiscarding'));
      console.error(err);
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !currentUser) {
      return;
    }

    const note = {
      text: noteText.trim(),
      createdBy: currentUser.email || currentUser.displayName || 'Unknown User',
      createdById: currentUser.uid || null,
      createdAt: Timestamp.now(),
    };

    try {
      await updateDoc(doc(db, 'found-items', id), {
        notes: arrayUnion(note),
      });

      setItem((prev) => prev ? ({
        ...prev,
        notes: Array.isArray(prev.notes) ? [...prev.notes, note] : [note],
      }) : prev);
      setNoteText('');
    } catch (err) {
      console.error('Failed to add note:', err);
      setError(t('foundItemDetails.messages.errorAddingNote'));
    }
  };

  const handleOwnerFieldChange = (event) => {
    const { name, value } = event.target;
    setOwnerDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOwnerEditCancel = () => {
    setOwnerDraft(toOwnerDraft(item));
    setOwnerEditMode(false);
  };

  const handleOwnerSave = async () => {
    if (!item || !canEditOwner || ownerSaving) {
      return;
    }

    const nextOwner = {
      firstName: ownerDraft.firstName.trim(),
      surname: ownerDraft.surname.trim(),
      dateLost: ownerDraft.dateLost || '',
      phone: ownerDraft.phone.trim(),
      email: ownerDraft.email.trim(),
      address: ownerDraft.address.trim(),
      country: ownerDraft.country.trim(),
      organisation: ownerDraft.organisation.trim(),
      updatedAt: Timestamp.now(),
    };

    if (nextOwner.email && !isValidEmail(nextOwner.email)) {
      setError(t('foundItemDetails.messages.invalidOwnerEmail'));
      return;
    }

    try {
      setOwnerSaving(true);
      await updateDoc(doc(db, 'found-items', id), nextOwner);
      setItem((prev) => prev ? { ...prev, ...nextOwner } : prev);
      setOwnerEditMode(false);
    } catch (err) {
      setError(t('foundItemDetails.messages.errorSavingOwnerInfo'));
      console.error('Failed to save owner info:', err);
    } finally {
      setOwnerSaving(false);
    }
  };

  const handleDirectReturn = async () => {
    if (!canCreateDirectReturn || !ownerInfoReadyForDirectReturn || directReturnSubmitting) {
      return;
    }

    try {
      setDirectReturnSubmitting(true);
      setError('');

      const response = await createDirectReturnFromFoundItem({
        foundItemId: id,
        ownershipConfirmed: true,
      });
      const payload = response?.data || response;

      if (!payload?.matchId) {
        throw new Error('Match ID missing from direct return response.');
      }

      setShowDirectReturnDialog(false);
      navigate(`/${hotelId}/matches/${payload.matchId}`);
    } catch (err) {
      console.error('Failed to create direct return:', err);
      const message = err?.message ? ` ${err.message}` : '';
      setError(`${t('foundItemDetails.messages.errorCreatingDirectReturn')}${message}`);
    } finally {
      setDirectReturnSubmitting(false);
    }
  };

  // Update the formatDate function to handle both Timestamp and Date objects
  const formatDate = (date) => {
    if (!date) return t('foundItemDetails.values.noData');
    
    try {
      // If it's a Firestore Timestamp
      if (date instanceof Timestamp) {
        return date.toDate().toLocaleDateString();
      }
      // If it's already a Date object
      if (date instanceof Date) {
        return date.toLocaleDateString();
      }
      // If it's a string or timestamp object from the database
      return new Date(date).toLocaleDateString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return t('foundItemDetails.values.invalidDate');
    }
  };

  if (loading) {
    return <div className="loading-container">{t('foundItemDetails.loading')}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="item-details-container">
      <div className="item-details-header">
        <button onClick={() => navigate(`/${hotelId}/admin`)} className="back-button">
          &larr; {item?.status === 'matched' || item?.status === 'disposed' ? 
            t('foundItemDetails.buttons.doneButton') : 
            t('foundItemDetails.buttons.backToList')}
        </button>
        <h2>{item?.status === 'disposed' ? t('foundItemDetails.titleDiscarded') : t('foundItemDetails.title')}</h2>
        <div className="header-actions">
          {(userClaims?.admin === true) && item.status === 'disposed' && (
            <button 
              onClick={() => setShowUndoDiscardDialog(true)} 
              className="undo-discard-btn"
            >
              {t('foundItemDetails.buttons.undoDiscard')}
            </button>
          )}
          {(userClaims?.admin === true) && item.status !== 'disposed' && item.status !== 'matched' && (
            <button 
              onClick={() => setShowDiscardDialog(true)} 
              className="discard-btn"
            >
              {t('foundItemDetails.buttons.markAsDiscarded')}
            </button>
          )}
          {(userClaims?.admin === true) && (
            <button 
              onClick={() => setShowDeleteDialog(true)} 
              className="delete-btn"
            >
              {t('foundItemDetails.buttons.deleteItem')}
            </button>
          )}
        </div>
      </div>
      
      {item && (
        <>
          {item.deleted && (
            <div className="deleted-banner">
              {t('foundItemDetails.messages.itemDeleted')}
            </div>
          )}
          <div className="item-details-content">
            <div className="detail-section">
              <h3>{t('foundItemDetails.sections.itemInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.itemType')}</div>
                  <div className="detail-value">{item.itemType || t('foundItemDetails.values.noData')}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.itemName')}</div>
                  <div className="detail-value">{item.itemName || t('foundItemDetails.values.noData')}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.dateFound')}</div>
                  <div className="detail-value">{formatDate(item.dateFound)}</div>
                </div>
                {item.roomNumber && (
                  <div className="detail-item">
                    <div className="detail-label">{t('foundItemDetails.fields.roomNumber')}</div>
                    <div className="detail-value">{item.roomNumber}</div>
                  </div>
                )}
                {item.hotelComplex && (
                  <div className="detail-item">
                    <div className="detail-label">{t('foundItemDetails.fields.hotelComplex')}</div>
                    <div className="detail-value">{item.hotelComplex}</div>
                  </div>
                )}
                {item.otherLocation && (
                  <div className="detail-item">
                    <div className="detail-label">{t('foundItemDetails.fields.otherLocation')}</div>
                    <div className="detail-value">{item.otherLocation}</div>
                  </div>
                )}
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.storageLocation')}</div>
                  <div className="detail-value">{item.storageLocation || t('foundItemDetails.values.noData')}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.cashValue')}</div>
                  <div className="detail-value">{item.cashValue || t('foundItemDetails.values.noData')}</div>
                </div>
                <div className="detail-item full-width">
                  <div className="detail-label">{t('foundItemDetails.fields.comments')}</div>
                  <div className="detail-value comment-text">{item.comments || t('foundItemDetails.values.noComments')}</div>
                </div>
              </div>
              
              {/* Add image display */}
              {item.imageUrls && item.imageUrls.length > 0 && (
                <div className="detail-item full-width">
                  <div className="detail-label">{t('foundItemDetails.fields.photos')}</div>
                  <div className="image-preview-container">
                    {item.imageUrls.map((url, index) => (
                      <div key={index} className="image-preview">
                        <img src={url} alt={t('foundItemDetails.alt.itemPhoto', { index: index + 1 })} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="detail-section">
              <h3>{t('foundItemDetails.sections.finderInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.finderName')}</div>
                  <div className="detail-value">{item.finderName || t('foundItemDetails.values.noData')}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.foundByUser')}</div>
                  <div className="detail-value">
                    {item.foundByUserEmail || t('foundItemDetails.values.noData')}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.foundByStaff')}</div>
                  <div className="detail-value">
                    {item.foundByStaff !== undefined 
                      ? (item.foundByStaff ? t('foundItemDetails.values.yes') : t('foundItemDetails.values.no'))
                      : t('foundItemDetails.values.notSpecified')
                    }
                  </div>
                </div>
                <div className="detail-item full-width">
                  <div className="detail-label">{t('foundItemDetails.fields.foundByNonStaff')}</div>
                  <div className="detail-value">{item.foundByNonHotelStaff || t('foundItemDetails.values.noData')}</div>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <div className="section-header">
                <h3>{t('foundItemDetails.sections.ownerInformation')}</h3>
                {canEditOwner && (
                  ownerEditMode ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={handleOwnerEditCancel}
                        disabled={ownerSaving}
                      >
                        {t('foundItemDetails.buttons.cancelOwnerEdit')}
                      </button>
                      <button
                        type="button"
                        className="add-button"
                        onClick={handleOwnerSave}
                        disabled={ownerSaving}
                      >
                        {ownerSaving
                          ? t('foundItemDetails.buttons.savingOwnerInfo')
                          : t('foundItemDetails.buttons.saveOwnerInfo')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="add-button"
                      onClick={() => setOwnerEditMode(true)}
                    >
                      {t('foundItemDetails.buttons.editOwnerInfo')}
                    </button>
                  )
                )}
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.firstName')}</div>
                  {ownerEditMode ? (
                    <input
                      type="text"
                      name="firstName"
                      value={ownerDraft.firstName}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.firstName || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.surname')}</div>
                  {ownerEditMode ? (
                    <input
                      type="text"
                      name="surname"
                      value={ownerDraft.surname}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.surname || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.dateLost')}</div>
                  {ownerEditMode ? (
                    <input
                      type="date"
                      name="dateLost"
                      value={ownerDraft.dateLost}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{formatDate(item.dateLost)}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.phone')}</div>
                  {ownerEditMode ? (
                    <input
                      type="text"
                      name="phone"
                      value={ownerDraft.phone}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.phone || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
                <div className="detail-item full-width">
                  <div className="detail-label">{t('foundItemDetails.fields.email')}</div>
                  {ownerEditMode ? (
                    <input
                      type="email"
                      name="email"
                      value={ownerDraft.email}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.email || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
                <div className="detail-item full-width">
                  <div className="detail-label">{t('foundItemDetails.fields.address')}</div>
                  {ownerEditMode ? (
                    <input
                      type="text"
                      name="address"
                      value={ownerDraft.address}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.address || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.country')}</div>
                  {ownerEditMode ? (
                    <input
                      type="text"
                      name="country"
                      value={ownerDraft.country}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.country || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
                <div className="detail-item full-width">
                  <div className="detail-label">{t('foundItemDetails.fields.organisation')}</div>
                  {ownerEditMode ? (
                    <input
                      type="text"
                      name="organisation"
                      value={ownerDraft.organisation}
                      onChange={handleOwnerFieldChange}
                      className="detail-input"
                    />
                  ) : (
                    <div className="detail-value">{item.organisation || t('foundItemDetails.values.noData')}</div>
                  )}
                </div>
              </div>
              {canCreateDirectReturn && (
                <div className="owner-direct-return-row">
                  <button
                    type="button"
                    className="direct-return-button"
                    onClick={() => setShowDirectReturnDialog(true)}
                    disabled={!ownerInfoReadyForDirectReturn || directReturnSubmitting}
                  >
                    {directReturnSubmitting
                      ? t('foundItemDetails.buttons.directReturnCreating')
                      : t('foundItemDetails.buttons.directReturn')}
                  </button>
                </div>
              )}
            </div>
            
            <div className="detail-section">
              <h3>{t('foundItemDetails.sections.systemInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.status')}</div>
                  <div className="detail-value status-badge">{t(`foundItemDetails.values.${item.status || 'pending'}`)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('foundItemDetails.fields.createdAt')}</div>
                  <div className="detail-value">
                    {item.createdAt ? new Date(item.createdAt.toDate()).toLocaleString() : t('foundItemDetails.values.noData')}
                  </div>
                </div>
                {item.matchedAt && (
                  <div className="detail-item">
                    <div className="detail-label">{t('foundItemDetails.fields.matchedAt')}</div>
                    <div className="detail-value">
                      {new Date(item.matchedAt.toDate()).toLocaleString()}
                    </div>
                  </div>
                )}
                {item.matchId && (
                  <div className="detail-item">
                    <div className="detail-label">{t('foundItemDetails.fields.matchDetails')}</div>
                    <div className="detail-value">
                      <span 
                        className="link-text"
                        onClick={() => navigate(`/${hotelId}/matches/${item.matchId}`)}
                        style={{ 
                          cursor: 'pointer',
                          color: '#2196f3',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('foundItemDetails.buttons.viewMatchDetails')}
                      </span>
                    </div>
                  </div>
                )}
                {item.status === 'disposed' && (
                  <>
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.discardedBy')}</div>
                      <div className="detail-value">{item.disposer || t('foundItemDetails.values.notSpecified')}</div>
                    </div>
                    {item.disposerEmail && (
                      <div className="detail-item">
                        <div className="detail-label">{t('foundItemDetails.fields.discardedByEmail')}</div>
                        <div className="detail-value">{item.disposerEmail}</div>
                      </div>
                    )}
                    {item.discardedAt && (
                      <div className="detail-item">
                        <div className="detail-label">{t('foundItemDetails.fields.discardedAt')}</div>
                        <div className="detail-value">
                          {new Date(item.discardedAt.toDate()).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>{t('foundItemDetails.sections.notes')}</h3>
              {Array.isArray(item.notes) && item.notes.length > 0 ? (
                <div className="detail-grid">
                  {item.notes.map((note, index) => (
                    <div key={`${note.createdAt?.seconds || index}-${index}`} className="detail-item full-width">
                      <div className="detail-value comment-text">
                        {note.text}
                      </div>
                      <div className="detail-label" style={{ marginTop: '6px' }}>
                        {note.createdBy || t('foundItemDetails.values.notSpecified')}
                        {note.createdAt ? ` • ${new Date(note.createdAt.toDate ? note.createdAt.toDate() : note.createdAt).toLocaleString()}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="detail-value">{t('foundItemDetails.values.noNotes')}</div>
              )}

              {isStaffOrAdmin && (
                <div style={{ marginTop: '12px' }}>
                  <label className="detail-label" htmlFor="found-item-note">
                    {t('foundItemDetails.fields.addNote')}
                  </label>
                  <textarea
                    id="found-item-note"
                    className="detail-input"
                    rows={3}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder={t('foundItemDetails.placeholders.note')}
                  />
                  <button
                    type="button"
                    className="add-button"
                    style={{ marginTop: '8px' }}
                    onClick={handleAddNote}
                    disabled={!noteText.trim()}
                  >
                    {t('foundItemDetails.buttons.addNote')}
                  </button>
                </div>
              )}
            </div>

            {/* Render PotentialMatches only when all props are ready and item is not matched and not discarded */}
            {(userClaims?.admin === true) && item && !item.matchId && item.status !== 'disposed' && hotelId && (
              <PotentialMatches item={item} itemType="found" onMatch={handleMatch} hotelId={hotelId} />
            )}

            {matchedLostItem && (
              <div className="detail-section matched-item-section">
                <div className="section-header">
                  <h3 
                    onClick={() => navigate(`/${hotelId}/lost-items/${matchedLostItem.id}`)}
                    style={{ 
                      cursor: 'pointer',
                      color: '#2196f3',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    {t('foundItemDetails.sections.matchedLostItem')}
                    <span style={{ fontSize: '0.8em' }}>→</span>
                  </h3>
                  {(userClaims?.admin === true) && canUnmatch && (
                    <button 
                      className="unmatch-button"
                      onClick={() => setShowUnmatchDialog(true)}
                    >
                      {t('foundItemDetails.buttons.unmatchItems')}
                    </button>
                  )}
                </div>

                {!isStaffOrAdmin && (
                  <div className="anon-summary-card">
                    <div className="summary-header">
                      <h4>{t('foundItemDetails.anon.summaryTitle')}</h4>
                      <p>{t('foundItemDetails.anon.summaryMessage')}</p>
                    </div>
                    <div className="summary-body">
                      {matchedLostItem.imageUrls && matchedLostItem.imageUrls.length > 0 ? (
                        <img
                          src={matchedLostItem.imageUrls[0]}
                          alt={t('foundItemDetails.alt.matchedItemPhoto', { index: 1 })}
                          className="summary-image"
                        />
                      ) : (
                        <div className="summary-placeholder">{t('foundItemDetails.values.noData')}</div>
                      )}
                      <div className="summary-meta">
                        <div>{t('foundItemDetails.fields.itemType')}: {matchedLostItem.itemType || t('foundItemDetails.values.noData')}</div>
                        <div>{t('foundItemDetails.fields.itemName')}: {matchedLostItem.itemName || t('foundItemDetails.values.noData')}</div>
                        <div>{t('foundItemDetails.fields.dateLost')}: {formatDate(matchedLostItem.dateLost)}</div>
                        <div>{t('foundItemDetails.fields.locationLost')}: {matchedLostItem.locationLost || t('foundItemDetails.values.unknown')}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="summary-toggle-button"
                      onClick={() => setShowMatchedDetails(prev => !prev)}
                    >
                      {showMatchedDetails ? t('foundItemDetails.anon.hideDetails') : t('foundItemDetails.anon.viewDetails')}
                    </button>
                  </div>
                )}

                {(isStaffOrAdmin || showMatchedDetails) && (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.itemType')}</div>
                      <div className="detail-value">{matchedLostItem.itemType || t('foundItemDetails.values.noData')}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.itemName')}</div>
                      <div className="detail-value">{matchedLostItem.itemName || t('foundItemDetails.values.noData')}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.dateLost')}</div>
                      <div className="detail-value">{formatDate(matchedLostItem.dateLost)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.locationLost')}</div>
                      <div className="detail-value">{matchedLostItem.locationLost || t('foundItemDetails.values.unknown')}</div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="detail-label">{t('foundItemDetails.fields.description')}</div>
                      <div className="detail-value comment-text">{matchedLostItem.description || t('foundItemDetails.values.noDescription')}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.contactName')}</div>
                      <div className="detail-value">{`${matchedLostItem.firstName || ''} ${matchedLostItem.surname || ''}`.trim() || t('foundItemDetails.values.noData')}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('foundItemDetails.fields.contactPhone')}</div>
                      <div className="detail-value">{matchedLostItem.phone || t('foundItemDetails.values.noData')}</div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="detail-label">{t('foundItemDetails.fields.contactEmail')}</div>
                      <div className="detail-value">{matchedLostItem.email || t('foundItemDetails.values.noData')}</div>
                    </div>
                    {matchedLostItem.imageUrls && matchedLostItem.imageUrls.length > 0 && (
                      <div className="detail-item full-width">
                        <div className="detail-label">{t('foundItemDetails.fields.photos')}</div>
                        <div className="image-preview-container">
                          {matchedLostItem.imageUrls.map((url, index) => (
                            <div key={index} className="image-preview">
                              <img src={url} alt={t('foundItemDetails.alt.matchedItemPhoto', { index: index + 1 })} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Add margin-top to create space */}
            {(showPackageSizeSelector || matchData?.packageSize) && (userClaims?.admin === true) && (
              <div style={{ marginTop: '20px' }}>
                {showPackageSizeSelector && canEditPackage ? (
                  <div className="detail-section">
                    <PackageSizeSelector
                      onSelect={handlePackageSizeSelect}
                      selectedSize={selectedPackageSize}
                    />
                  </div>
                ) : matchData.packageSize ? (
                  <div className="detail-section" onClick={() => canEditPackage && setShowPackageSizeSelector(true)} style={{ cursor: canEditPackage ? 'pointer' : 'default' }}>
                    <h3>{t('foundItemDetails.sections.packageInformation')}</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <div className="detail-label">{t('foundItemDetails.fields.packageSize')}</div>
                        <div className="detail-value status-badge">{matchData.packageSize}</div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-label">{t('foundItemDetails.fields.destination')}</div>
                        <div className="detail-value">
                          <span style={{ marginRight: '6px' }}>
                            {getCountryFlag(destinationCountryCode || destinationCountryName)}
                          </span>
                          {destinationCountryName}
                        </div>
                      </div>
                      {showPricing && (
                        <>
                          <div className="detail-item">
                            <div className="detail-label">{t('foundItemDetails.fields.shippingCost')}</div>
                            <div className="detail-value">
                              {shippingQuoteLoading
                                ? t('payment.messages.loading')
                                : typeof shippingCostValue === 'number'
                                  ? `¥${shippingCostValue.toLocaleString()}`
                                  : shippingQuoteError || t('foundItemDetails.values.noData')}
                            </div>
                          </div>
                          <div className="detail-item">
                            <div className="detail-label">{t('foundItemDetails.fields.quikbakFee')}</div>
                            <div className="detail-value">
                              {typeof quikbakFeeValue === 'number'
                                ? `¥${quikbakFeeValue.toLocaleString()}`
                                : t('foundItemDetails.values.noData')}
                            </div>
                          </div>
                          <div className="detail-item">
                            <div className="detail-label">{t('foundItemDetails.fields.totalCharge')}</div>
                            <div className="detail-value">
                              {typeof totalChargeValue === 'number'
                                ? `¥${totalChargeValue.toLocaleString()}`
                                : t('foundItemDetails.values.noData')}
                            </div>
                          </div>
                        </>
                      )}
                      {matchData.readyToShipCode && (
                        <div className="detail-item">
                          <div className="detail-label">{t('foundItemDetails.fields.readyToShipCode')}</div>
                          <div className="detail-value">{matchData.readyToShipCode}</div>
                        </div>
                      )}
                    </div>
                    {matchData.shippingAddress && (
                      <div className="detail-value" style={{ marginTop: '12px' }}>
                        <strong>{t('foundItemDetails.fields.shippingAddress')}</strong>
                        <div>{matchData.shippingAddress.name}</div>
                        <div>{matchData.shippingAddress.line1}</div>
                        {matchData.shippingAddress.line2 && matchData.shippingAddress.line2.trim() !== '' && (
                          <div>{matchData.shippingAddress.line2}</div>
                        )}
                        <div>
                          {[matchData.shippingAddress.city, matchData.shippingAddress.state]
                            .filter(Boolean)
                            .join(', ')}{matchData.shippingAddress.postalCode ? ` ${matchData.shippingAddress.postalCode}` : ''}
                        </div>
                        <div>
                          <span style={{ marginRight: '6px' }}>
                            {getCountryFlag(matchData.shippingAddress.countryCode || matchData.shippingAddress.country)}
                          </span>
                          {getCountryNameFromCode(matchData.shippingAddress.countryCode) || matchData.shippingAddress.country}
                        </div>
                        {matchData.shippingAddress.phone && matchData.shippingAddress.phone.trim() !== '' && (
                          <div>{matchData.shippingAddress.phone}</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('foundItemDetails.confirmations.deleteTitle')}
        message={t('foundItemDetails.confirmations.deleteMessage')}
      />

      <ConfirmationDialog
        isOpen={showUnmatchDialog}
        onClose={() => setShowUnmatchDialog(false)}
        onConfirm={handleUnmatch}
        title={t('foundItemDetails.confirmations.unmatchTitle')}
        message={t('foundItemDetails.confirmations.unmatchMessage')}
      />

      <ConfirmationDialog
        isOpen={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        onConfirm={handleDiscard}
        title={t('foundItemDetails.confirmations.discardTitle')}
        message={t('foundItemDetails.confirmations.discardMessage')}
      />

      <ConfirmationDialog
        isOpen={showUndoDiscardDialog}
        onClose={() => setShowUndoDiscardDialog(false)}
        onConfirm={handleUndoDiscard}
        title={t('foundItemDetails.confirmations.undoDiscardTitle')}
        message={t('foundItemDetails.confirmations.undoDiscardMessage')}
      />

      <ConfirmationDialog
        isOpen={showDirectReturnDialog}
        onClose={() => {
          if (!directReturnSubmitting) {
            setShowDirectReturnDialog(false);
          }
        }}
        onConfirm={handleDirectReturn}
        title={t('foundItemDetails.confirmations.directReturnTitle')}
        message={t('foundItemDetails.confirmations.directReturnMessage')}
      />
    </div>
  );
} 
